import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useLang } from '../i18n/LanguageContext';

export default function Register() {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { register } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();

  const getPasswordStrength = (pwd) => {
    if (!pwd) return null;
    if (pwd.length < 6) return 'weak';
    if (pwd.length < 10 || !/\d/.test(pwd)) return 'medium';
    return 'strong';
  };

  const passwordStrength = getPasswordStrength(password);

  const location = useLocation();
  const from = location.state?.from?.pathname || '/';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (password !== confirmPassword) {
      setError(t('compte.inscription.erreurMotsDePasse'));
      return;
    }

    if (!agreeToTerms) {
      setError(t('compte.inscription.erreurConditions'));
      return;
    }

    if (passwordStrength === 'weak') {
      setError(t('compte.inscription.erreurLongueur'));
      return;
    }

    setIsLoading(true);

    try {
      await register(fullName, email, password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(err.message || t('compte.inscription.erreur'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 md:p-8 bg-gradient-to-br from-[#F1F2E9] via-[#F2EFE4] to-[#FEFEFA] relative overflow-hidden">
      <Helmet>
        <title>{t('compte.inscription.seoTitre')} | Mirum Games</title>
        <meta name="description" content={t('compte.inscription.seoDescription')} />
      </Helmet>

      {/* Decorative circles */}
      <div className="fixed top-[-50px] left-[-50px] w-[300px] h-[300px] rounded-full bg-gradient-to-br from-[#E1A72E] to-[#E8C36A] opacity-10 pointer-events-none -z-10" />
      <div className="fixed bottom-[-50px] right-[-50px] w-[300px] h-[300px] rounded-full bg-gradient-to-br from-[#DCEAE4] to-[#E1A72E] opacity-10 pointer-events-none -z-10" />

      <div className="w-full max-w-[450px] bg-[#FEFEFA] border-3 border-[#DCEAE4] rounded-2xl p-6 md:p-8 shadow-[0_20px_60px_rgba(139,111,71,0.2)] animate-[fadeInUp_0.8s_ease-out] relative z-10">
        {/* Top decoration circle */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20 bg-gradient-to-br from-[#E1A72E] to-[#DCEAE4] rounded-full shadow-[0_10px_30px_rgba(212,175,55,0.3)] flex items-center justify-center" />

        {/* Header */}
        <div className="flex flex-col items-center gap-4 md:gap-6 text-center mb-6 md:mb-8 mt-4 md:mt-6">
          <div className="text-6xl md:text-[60px] animate-[floatGentle_2s_ease-in-out_infinite]">✨</div>
          <h1 className="m-0 text-2xl md:text-[32px] font-black text-[#1F3833] uppercase tracking-tight font-['Fredoka',_'Poppins',_sans-serif]">{t('compte.inscription.titre')}</h1>
          <p className="m-0 text-sm text-[#2F7E72] italic">{t('compte.inscription.sousTitre')}</p>
        </div>

        {/* Form */}
        <form className="flex flex-col gap-4 md:gap-6 animate-[fadeInUp_0.8s_ease-out_0.1s_backwards]" onSubmit={handleSubmit}>
          {/* Error Message */}
          {error && (
            <div className="p-4 rounded-lg bg-red-100/15 border-2 border-red-400 text-red-800 text-[13px] font-semibold text-center animate-[scaleIn_0.3s_ease]">
              {error}
            </div>
          )}

          {/* Full Name Input */}
          <div className="flex flex-col gap-2">
            <label htmlFor="fullName" className="text-[13px] font-semibold text-[#1F3833] uppercase tracking-wide">
              {t('compte.champ.nomComplet')}
            </label>
            <input
              id="fullName"
              type="text"
              required
              className="px-4 py-3 md:py-[14px] border-2 border-[#DCEAE4] rounded-lg text-sm bg-[#FEFEFA] text-[#1F3833] transition-all duration-300 placeholder:text-[#8FB0A8] focus:outline-none focus:border-[#E1A72E] focus:shadow-[0_0_0_4px_rgba(212,175,55,0.1)] focus:bg-[#F2EFE4]"
              placeholder={t('compte.champ.nomCompletPlaceholder')}
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              disabled={isLoading}
            />
          </div>

          {/* Email Input */}
          <div className="flex flex-col gap-2">
            <label htmlFor="email" className="text-[13px] font-semibold text-[#1F3833] uppercase tracking-wide">
              {t('compte.champ.email')}
            </label>
            <input
              id="email"
              type="email"
              required
              className="px-4 py-3 md:py-[14px] border-2 border-[#DCEAE4] rounded-lg text-sm bg-[#FEFEFA] text-[#1F3833] transition-all duration-300 placeholder:text-[#8FB0A8] focus:outline-none focus:border-[#E1A72E] focus:shadow-[0_0_0_4px_rgba(212,175,55,0.1)] focus:bg-[#F2EFE4]"
              placeholder={t('compte.champ.emailPlaceholder')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
            />
          </div>

          {/* Password Input */}
          <div className="flex flex-col gap-2">
            <label htmlFor="password" className="text-[13px] font-semibold text-[#1F3833] uppercase tracking-wide">
              {t('compte.champ.motDePasse')}
            </label>
            <input
              id="password"
              type="password"
              required
              className="px-4 py-3 md:py-[14px] border-2 border-[#DCEAE4] rounded-lg text-sm bg-[#FEFEFA] text-[#1F3833] transition-all duration-300 placeholder:text-[#8FB0A8] focus:outline-none focus:border-[#E1A72E] focus:shadow-[0_0_0_4px_rgba(212,175,55,0.1)] focus:bg-[#F2EFE4]"
              placeholder={t('compte.champ.creerMotDePasse')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
            />

            {/* Password Strength Indicator */}
            {password && (
              <div className="flex flex-col gap-2">
                <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden relative">
                  <div className={`h-full transition-all duration-300 rounded-full ${passwordStrength === 'weak' ? 'w-1/3 bg-red-400' :
                      passwordStrength === 'medium' ? 'w-2/3 bg-yellow-400' :
                        'w-full bg-[#E1A72E]'
                    }`} />
                </div>
                <span className={`text-[11px] font-semibold uppercase tracking-wide ${passwordStrength === 'weak' ? 'text-red-400' :
                    passwordStrength === 'medium' ? 'text-yellow-500' :
                      'text-[#E1A72E]'
                  }`}>
                  {passwordStrength === 'weak' && t('compte.force.faible')}
                  {passwordStrength === 'medium' && t('compte.force.moyen')}
                  {passwordStrength === 'strong' && t('compte.force.fort')}
                </span>
              </div>
            )}
          </div>

          {/* Confirm Password Input */}
          <div className="flex flex-col gap-2">
            <label htmlFor="confirmPassword" className="text-[13px] font-semibold text-[#1F3833] uppercase tracking-wide">
              {t('compte.champ.confirmation')}
            </label>
            <input
              id="confirmPassword"
              type="password"
              required
              className="px-4 py-3 md:py-[14px] border-2 border-[#DCEAE4] rounded-lg text-sm bg-[#FEFEFA] text-[#1F3833] transition-all duration-300 placeholder:text-[#8FB0A8] focus:outline-none focus:border-[#E1A72E] focus:shadow-[0_0_0_4px_rgba(212,175,55,0.1)] focus:bg-[#F2EFE4]"
              placeholder={t('compte.champ.confirmationPlaceholder')}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={isLoading}
            />
          </div>

          {/* Terms & Conditions Checkbox */}
          <div className="flex items-start gap-2">
            <input
              type="checkbox"
              className="w-4 h-4 cursor-pointer mt-0.5"
              checked={agreeToTerms}
              onChange={(e) => setAgreeToTerms(e.target.checked)}
              disabled={isLoading}
            />
            <label className="text-xs text-[#1F3833] leading-relaxed cursor-pointer">
              {t('compte.inscription.accepteLes')}{' '}
              <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-[#E1A72E] no-underline font-bold hover:underline">{t('compte.inscription.conditions')}</a>
              {' '}{t('compte.inscription.etLa')}{' '}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-[#E1A72E] no-underline font-bold hover:underline">{t('compte.inscription.politique')}</a>
            </label>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="px-6 py-3 md:py-[14px] border-none rounded-lg text-sm font-bold uppercase tracking-wide cursor-pointer transition-all duration-300 bg-gradient-to-br from-[#E1A72E] to-[#DCEAE4] text-white shadow-[0_8px_20px_rgba(212,175,55,0.2)] hover:not(:disabled):-translate-y-0.5 hover:not(:disabled):shadow-[0_12px_30px_rgba(212,175,55,0.4)] active:not(:disabled):translate-y-0 disabled:opacity-60 disabled:cursor-not-allowed"
            disabled={isLoading || !agreeToTerms}
          >
            {isLoading ? t('compte.inscription.enCours') : t('compte.inscription.bouton')}
          </button>
        </form>

        {/* Footer Links */}
        <div className="flex flex-col gap-3 md:gap-4 text-center mt-6 md:mt-8 pt-4 md:pt-6 border-t-2 border-[#DCEAE4]">
          <p className="text-[13px] text-[#1F3833] m-0">
            {t('compte.inscription.dejaUnCompte')}{' '}
            <Link to="/login" className="text-[#E1A72E] no-underline font-bold transition-all duration-300 hover:text-[#DCEAE4] hover:underline">{t('compte.inscription.seConnecter')}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
